import React, { useEffect } from 'react';
import { useAuth } from '../context/AuthContext';
import {
  canShowAlcohol68,
  canShowAlcohol70,
  canShowAlcohol72,
  resolveDisplaySettings
} from '../utils/displaySettings';
import './DashboardStats.css';

const DashboardStats = ({ stats, alcoholStats }) => {
  const { user, refreshUser } = useAuth();
  const displaySettings = resolveDisplaySettings(user);

  useEffect(() => {
    if (refreshUser) refreshUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (!stats) {
    return (
      <div style={{ 
        display: 'flex', 
        justifyContent: 'center', 
        alignItems: 'center', 
        minHeight: '200px',
        fontSize: '0.875rem',
        color: '#6c757d',
        fontWeight: '400'
      }}>
        Loading stats...
      </div>
    );
  }

  const statCards = [];

  if (stats.totalClients !== undefined) {
    statCards.push({ 
      label: 'Total Clients', 
      value: stats.totalClients, 
      color: '#0F2854',
      icon: ''
    });
  }
  if (stats.totalManagers !== undefined) {
    statCards.push({ 
      label: 'Total Managers', 
      value: stats.totalManagers, 
      color: '#3F9AAE',
      icon: ''
    });
  }
  if (stats.totalEmployees !== undefined) {
    statCards.push({ 
      label: 'Total Employees', 
      value: stats.totalEmployees, 
      color: '#10b981',
      icon: ''
    });
  }
  if (stats.totalReadings !== undefined) {
    statCards.push({ 
      label: 'Total Tests', 
      value: stats.totalReadings, 
      color: '#6366f1',
      icon: ''
    });
  }
  if (stats.activeDevices !== undefined) {
    statCards.push({ 
      label: 'Active Devices', 
      value: stats.activeDevices, 
      color: '#8b5cf6',
      icon: ''
    });
  }
  if (stats.adulteratedSamples !== undefined) {
    statCards.push({ 
      label: 'Adulterated Samples', 
      value: stats.adulteratedSamples, 
      color: '#ef4444',
      icon: ''
    });
  }
  if (stats.nonAdulteratedSamples !== undefined) {
    statCards.push({ 
      label: 'Non-Adulterated Samples', 
      value: stats.nonAdulteratedSamples, 
      color: '#10b981',
      icon: ''
    });
  }

  // Add alcohol percentage stats if available
  if (alcoholStats) {
    const v2 = alcoholStats.alcoholV2;
    const mergeV2 = (legacy) => {
      if (!v2 || !v2.total) return legacy;
      if (!legacy || !legacy.total) {
        return {
          pass: v2.pass || 0,
          fail: v2.fail || 0,
          refer: v2.refer || 0,
          total: v2.total
        };
      }
      return {
        pass: (legacy.pass || 0) + (v2.pass || 0),
        fail: (legacy.fail || 0) + (v2.fail || 0),
        refer: v2.refer || 0,
        total: (legacy.total || 0) + v2.total
      };
    };

    const pushAlcoholCard = (label, data) => {
      if (!data || !data.total) return;
      const referPart = data.refer ? ` | refer: ${data.refer}` : '';
      statCards.push({
        label,
        value: `${data.pass}/${data.total}`,
        color: data.pass >= data.fail ? '#10b981' : '#ef4444',
        icon: '',
        subLabel: `Pass: ${data.pass} | Fail: ${data.fail}${referPart}`
      });
    };

    if (alcoholStats.alcohol60 && alcoholStats.alcohol60.total > 0) {
      pushAlcoholCard('60% Alcohol Test', alcoholStats.alcohol60);
    }
    if (alcoholStats.alcohol65 && alcoholStats.alcohol65.total > 0) {
      pushAlcoholCard('65% Alcohol Test', alcoholStats.alcohol65);
    }
    if (canShowAlcohol68(displaySettings)) {
      pushAlcoholCard('68% Alcohol Test', mergeV2(alcoholStats.alcohol68));
    }
    if (canShowAlcohol70(displaySettings)) {
      pushAlcoholCard('70% Alcohol Test', mergeV2(alcoholStats.alcohol70));
    }
    if (canShowAlcohol72(displaySettings)) {
      pushAlcoholCard('72% Alcohol Test', mergeV2(alcoholStats.alcohol72));
    }
  }

  return (
    <div className="dashboard-stats">
      <h1>Dashboard Overview</h1>
      <div className="stats-grid">
        {statCards.map((stat, index) => (
          <div key={index} className="stat-card" style={{ borderLeftColor: stat.color }}>
            <div className="stat-value">
              <span>{stat.value}</span>
              {stat.icon && <span className="stat-icon">{stat.icon}</span>}
            </div>
            <div className="stat-label">{stat.label}</div>
            {stat.subLabel && (
              <div className="stat-sublabel">
                {stat.subLabel}
              </div>
            )}
          </div>
        ))}
      </div>
    </div>
  );
};

export default DashboardStats;

